/**
 * ParticleWebGLLayer.tsx
 * غلاف React لمحرّك جسيمات الرياح بـ WebGL (ParticleGLLayer).
 */

import { useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { ParticleGLLayer, type ParticleSettings } from './webgl/ParticleGLLayer';
import { getWeatherInsertBeforeId } from './webgl/layerOrder';
import type { WeatherGrid } from '../../services/weatherGridService';
import { NATURAL_PARTICLE_PRESET } from '../../config/layerAnimation';
import { subscribePlayback } from '../../services/playbackClock';

// الإعداد البصري الافتراضي = ضبط Zoom Earth المعايَر (NATURAL_PARTICLE_PRESET هو مصدر
// الحقيقة؛ يصل عادةً عبر settings من layerAnimationSettings ويطغى على هذه القيم).
const ZOOM_EARTH_PARTICLE_SETTINGS: Partial<ParticleSettings> = Object.freeze({
    ...NATURAL_PARTICLE_PRESET,
});

interface ParticleWebGLLayerProps {
    id: string;
    /** شبكات الساعات المحيطة بالزمن الحالي — المزج بينها يجري على الـ GPU لكل إطار. */
    windGridPrev: WeatherGrid | null;
    windGrid: WeatherGrid | null;
    windGridNext: WeatherGrid | null;
    /** فهرس الساعة وكسرها من الشريط الزمني (متقطّع بخطوات 1/6؛ الطبقة تُنعّمه). */
    timeIndex: number;
    frameFraction: number;
    /**
     * يفعّل قراءة الكسر **المستمرّ** من ساعة التشغيل بدل القيمة المكمَّمة القادمة عبر
     * React. يُمرَّر true فقط حين يطابق `timeIndex` فهرسَ ساعة التشغيل (محور التوقّعات)؛
     * على محور القمر الصناعي تُشتقّ ساعة الرياح من زمن مطلق آخر فلا يصحّ الربط المباشر.
     */
    liveTime?: boolean;
    settings?: Partial<ParticleSettings>;
    darkMode?: boolean;
}

export function ParticleWebGLLayer({
    id, windGridPrev, windGrid, windGridNext, timeIndex, frameFraction, liveTime = false, settings, darkMode = true,
}: ParticleWebGLLayerProps) {
    const mapRef     = useMapRef();
    const layerRef   = useRef<ParticleGLLayer | null>(null);
    const reassertRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();

        let cancelled = false;
        // الكثافة تُحسب تلقائياً من الزووم داخل ParticleGLLayer في كل إطار
        const layerSettings = settings ? { ...ZOOM_EARTH_PARTICLE_SETTINGS, ...settings } : ZOOM_EARTH_PARTICLE_SETTINGS;
        const layer = new ParticleGLLayer(id, layerSettings, darkMode);

        const add = () => {
            if (cancelled) return;
            try {
                if (!map.getLayer(id)) map.addLayer(layer, getWeatherInsertBeforeId(map));
                layerRef.current = layer;
                layer.setWindGrids(windGridPrev, windGrid, windGridNext, timeIndex);
                layer.setModelTime(timeIndex, frameFraction);
            } catch (e) {
                console.error(`ParticleWebGLLayer(${id}): تعذّر إضافة الطبقة`, e);
            }
        };

        // أي طبقة طقس أخرى (حرارة/رياح/أقمار صناعية...) قد تُدرَج أو تُعاد بعد الجسيمات
        // فتعلوها وتخفيها. نتحقّق عند كل styledata من أن الجسيمات لا تزال أعلى طبقة طقس
        // (أي أنها الطبقة الموضوعة مباشرةً قبل مرساة الخريطة الأساسية الثابتة)، وإن لم
        // تكن كذلك نُعيد رفعها — لكن فقط عند الحاجة الفعلية، وإلّا فإن moveLayer نفسه
        // يُطلق styledata = حلقة لانهائية.
        reassertRef.current = () => {
            try {
                if (!map.getLayer(id)) return;
                const anchor = getWeatherInsertBeforeId(map);
                const layers = map.getStyle()?.layers ?? [];
                const anchorIdx = anchor ? layers.findIndex((l) => l.id === anchor) : layers.length;
                const pIdx = layers.findIndex((l) => l.id === id);
                if (pIdx === -1 || anchorIdx === -1) return;
                const isTopmost = pIdx === anchorIdx - 1;
                if (isTopmost) return; // الجسيمات أصلاً أعلى طبقات الطقس → لا تحريك → لا حلقة
                map.moveLayer(id, anchor);
            } catch { /* تجاهل */ }
        };
        map.on('styledata', reassertRef.current);

        const onStyleData = () => { if (map.isStyleLoaded()) { map.off('styledata', onStyleData); add(); } };
        if (map.isStyleLoaded()) add();
        else map.on('styledata', onStyleData);

        return () => {
            cancelled = true;
            map.off('styledata', onStyleData);
            if (reassertRef.current) map.off('styledata', reassertRef.current);
            reassertRef.current = null;
            layerRef.current = null;
            try {
                if (map.getLayer(id)) map.removeLayer(id);
            } catch { /* تجاهل أخطاء الإزالة */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef, id]);

    useEffect(() => {
        layerRef.current?.setWindGrids(windGridPrev, windGrid, windGridNext, timeIndex);
    }, [windGridPrev, windGrid, windGridNext, timeIndex]);
    useEffect(() => {
        if (liveTime) return;   // القناة المستمرّة أدناه هي المصدر حينئذٍ
        layerRef.current?.setModelTime(timeIndex, frameFraction);
    }, [timeIndex, frameFraction, liveTime]);

    /**
     * الزمن المستمرّ من ساعة التشغيل — الجسيمات واللون على قناة واحدة.
     *
     * أهمّيته ليست في نعومة الجسيمات فقط (لها مُنعّم داخلي في ParticleGLLayer)، بل في
     * **التزامن**: لو تحرّك اللون بقيمة عشرية مستمرّة بينما يتلقّى حقل الرياح ستّ درجات
     * مكمَّمة، لتقدّم أحدهما على الآخر داخل الساعة الواحدة فبدت الخيوط تنساب فوق حقل
     * لوني لا يوافقها. القناة نفسها تُلغي الانحراف من أصله.
     */
    useEffect(() => {
        if (!liveTime) return;
        return subscribePlayback((p) => {
            layerRef.current?.setModelTime(p.index, p.fraction);
        });
    }, [liveTime]);
    useEffect(() => { layerRef.current?.setDarkMode(darkMode); }, [darkMode]);
    // تغييرات لوحة الإعدادات تصل إلى الطبقة القائمة (كانت تُقرأ عند الإنشاء فقط، فلا
    // يتغيّر شيء حتى إعادة تركيب المكوّن).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (settings) layerRef.current?.setSettings(settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.density, settings?.speed, settings?.trail, settings?.opacity,
        settings?.lineWidth, settings?.lineWidthVariation, settings?.lifetime, settings?.color]);
    return null;
}
